export const questionnaireSchema = [
    {
      id: 'personal_goals',
      title: 'Personal and Business Goals',
      description: 'This section focuses on understanding your current situation, motivations, and goals related to succession planning.',
      questions: [
        {
          id: 'short_term_goals',
          question: 'What are your short-term (1-2 years) personal goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider goals related to work-life balance, health, relationships, and personal development. What do you want to achieve or improve in the near future?',
        },
        {
          id: 'mid_term_goals',
          question: 'What are your mid-term (3-5 years) personal goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Think about where you\'d like to be a few years from now. How might your priorities shift? What milestones would you like to reach?',
        },
        {
          id: 'long_term_goals',
          question: 'What are your long-term (5+ years) personal goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider your vision for retirement, legacy, and long-term fulfillment. What do you want your life to look like in the later stages?',
        },
        {
          id: 'short_term_business_goals',
          question: 'What are your short-term (1-2 years) business goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Focus on immediate business priorities like growth targets, operational improvements, client acquisition, or team development.',
        },
        {
          id: 'mid_term_business_goals',
          question: 'What are your mid-term (3-5 years) business goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider how you want the business to evolve over the next few years. What market position, financial targets, or structural changes are you aiming for?',
        },
        {
          id: 'long_term_business_goals',
          question: 'What are your long-term (5+ years) business goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Envision the ultimate destination for your business. What legacy do you want it to have? What should its position and impact be in the long run?',
        },
        {
          id: 'personal_goals_after_transition',
          question: 'What are your personal goals for the next stage of your life after transitioning from your current role in the business? (e.g., travel, hobbies, family, new ventures)',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Imagine your life beyond your current business role. What activities, relationships, or pursuits will bring you fulfillment and purpose?',
        },
        {
          id: 'time_occupation_after_business',
          question: 'How do you plan to spend the time that was previously occupied by your business?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider how you\'ll fill your days. Think about hobbies, volunteer work, family time, travel, or new professional endeavors that might replace business activities.',
        },
        {
          id: 'ideal_exit_timing',
          question: 'When would you ideally like to slow down or completely exit the business?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Be as specific as possible about your timeline, even if it might change. Consider your age, financial readiness, and personal circumstances.',
        },
        {
          id: 'transition_timeframe',
          question: 'When would you ideally like to transition out of your business?',
          type: 'radio',
          required: true,
          options: [
            { value: 'less_than_2', label: 'Less than 2 years' },
            { value: '2_3_years', label: '2 – 3 years' },
            { value: '3_5_years', label: '3 – 5 years' },
            { value: '5_10_years', label: '5 – 10 years' },
            { value: '10_plus_years', label: '10+ years' },
          ],
          hint: 'Select the timeframe that best aligns with your vision for transitioning leadership and ownership of your business.',
        },
        {
          id: 'changing_priorities',
          question: 'What are your personal priorities changing? Should you revise your personal goals?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Reflect on how your priorities may be shifting with age, family circumstances, health considerations, or changing interests.',
        },
        {
          id: 'lifestyle_changes',
          question: 'Will your lifestyle remain the same (i.e., do you want to do more travelling, change where you live or provide financial help to your children or grandchildren)?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider how your daily life, living situation, and financial commitments might change after transitioning from your business.',
        },
        {
          id: 'emotional_implications',
          question: 'When you are no longer the owner of a business, what will be the emotional implications for you?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Many business owners experience significant identity shifts when they step away. Consider how your sense of purpose, identity, and fulfillment might change.',
        },
        {
          id: 'transitional_role_implications',
          question: 'If the break from your business will take place over a period of time, what are the implications for you in this new role (i.e., giving up control, spending less time at the office, increased commitment required by the new owners)?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Think about how you\'ll adapt to a changing role with potentially less authority, different responsibilities, or reduced involvement in day-to-day operations.',
        },
      ],
    },
    {
      id: 'current_status',
      title: 'Current Succession Plan Status',
      description: 'This section assesses the current state of your succession planning efforts.',
      questions: [
        {
          id: 'plan_status',
          question: 'What is the current status of your transition/succession plan?',
          type: 'radio',
          required: true,
          options: [
            { value: 'written_current', label: 'Written down and up-to-date' },
            { value: 'mental_current', label: 'In my head and up-to-date' },
            { value: 'written_outdated', label: 'Written down but requires updating' },
            { value: 'mental_outdated', label: 'In my head but requires updating' },
            { value: 'wish_had', label: 'Something I wish I had' },
            { value: 'not_needed', label: "Something I don't need" },
          ],
          hint: 'Be honest about where you currently stand with succession planning. Recognizing your starting point is key to making progress.',
        },
        {
          id: 'plan_benefits',
          question: 'How do you think a transition/succession plan can help you? (Select all that apply)',
          type: 'checkbox',
          required: true,
          options: [
            { value: 'control_exit', label: 'Take control of my eventual exit from the business' },
            { value: 'maximize_value', label: 'Maximize the value of the business when I exit/transition' },
            { value: 'fund_retirement', label: 'Ensure I can fund my own retirement' },
            { value: 'peace_of_mind', label: 'Give my family, clients and staff confidence and peace of mind' },
            { value: 'legacy', label: 'Ensure my legacy is what I want it to be' },
            { value: 'not_needed', label: "Actually, I don't need a transition/succession plan" },
          ],
          hint: 'Understanding your motivations for succession planning will help guide your priorities throughout the process.',
        },
      ],
    },
    {
      id: 'planning_barriers',
      title: 'Reasons for Not Planning and Succession Planning Myths',
      description: 'This section explores potential reasons for not having a plan and addresses common misconceptions.',
      questions: [
        {
          id: 'no_plan_reasons',
          question: "I don't have a written transition/succession plan because I am: (Check all that apply)",
          type: 'checkbox',
          required: true,
          options: [
            { value: 'too_busy', label: 'Too busy' },
            { value: 'too_young', label: 'Too young' },
            { value: 'too_important', label: 'Too important to my business' },
            { value: 'too_attached', label: 'Too attached to my business' },
            { value: 'unsure_after', label: 'Unsure what I would do after' },
            { value: 'negative_reaction', label: 'Concerned clients and staff will react negatively' },
            { value: 'successor_conflict', label: 'Concerned about conflict among potential successors' },
            { value: 'not_sure_how', label: 'Not sure how to develop a plan' },
            { value: 'not_concerned_price', label: "Not concerned about selling my business at a good price when I'm ready" },
            { value: 'already_have', label: 'Not applicable (I already have a transition plan in place)' },
          ],
          hint: 'Identifying the barriers that have prevented you from planning can help you overcome them. Be honest about what has held you back.',
        },
        {
          id: 'buyers_belief',
          question: 'Do you believe that there will be lots of buyers for your practice when you are ready to retire? (Note: Consider that many buyers might not fit your requirements.)',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure' },
          ],
          hint: 'The reality is that finding the right buyer can take significant time. High-quality matches between sellers and buyers are less common than many expect.',
        },
        {
          id: 'career_end_view',
          question: 'Do you view succession as the end of your career? (Note: Succession is often a process over a number of years.)',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure' },
          ],
          hint: 'Many business owners continue in advisory roles or find new endeavors after succession. This can be a transition to a new chapter rather than an ending.',
        },
        {
          id: 'planning_timeframe',
          question: 'Do you believe that two years is plenty of time to plan your succession? (Note: Some plans can take 5-10 years.)',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure' },
          ],
          hint: 'Succession planning often takes longer than expected. Starting early gives you more options and better outcomes.',
        },
      ],
    },
    {
      id: 'selling_motivations',
      title: 'Motivations for Selling',
      description: 'This section helps understand what might prompt you to consider selling your practice.',
      questions: [
        {
          id: 'selling_motivators',
          question: 'What might motivate you to consider selling your practice? (Check all that apply)',
          type: 'checkbox',
          required: true,
          options: [
            { value: 'good_price', label: 'The opportunity to cash in at a good price' },
            { value: 'tired', label: 'Being tired of the business' },
            { value: 'mentoring', label: 'Mentoring someone else' },
            { value: 'lifestyle_change', label: 'Wanting a different lifestyle (e.g., more travel, hobbies, etc.)' },
            { value: 'health', label: 'Health concerns' },
            { value: 'family_issues', label: 'Family issues (e.g., caregiving, divorce, death of spouse, etc.)' },
          ],
          hint: 'Understanding your key motivators will help you recognize when the time is right to begin the transition process.',
        },
        {
          id: 'other_motivations',
          question: 'Are there any other factors that might motivate you to sell your practice?',
          type: 'text',
          required: false,
          multiline: true,
          hint: 'Consider unique personal circumstances or industry-specific factors that might influence your decision to sell.',
        },
      ],
    },
    {
      id: 'business_assessment',
      title: 'Self-Assessment and Business Perspective',
      description: 'This section focuses on your perspective on your business and your readiness for transition.',
      questions: [
        {
          id: 'would_buy_own',
          question: 'Would you buy your own practice right now?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ],
          hint: 'This question helps you see your business through a potential buyer\'s eyes. If your answer is no, it indicates areas that need improvement.',
        },
        {
          id: 'would_buy_own_explanation',
          question: 'If you answered "No" to the previous question, please explain why',
          type: 'text',
          required: false,
          multiline: true,
          conditional: {
            dependsOn: 'would_buy_own',
            showIf: 'no',
          },
          hint: 'Identifying specific concerns can highlight the most important areas to address before selling.',
        },
        {
          id: 'could_sell_to',
          question: 'To whom could you sell your practice today?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider all potential buyers in your network - employees, competitors, family members, or third parties. Be specific rather than general.',
        },
        {
          id: 'would_sell_to',
          question: 'To whom would you sell your practice today?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'This differs from who you could sell to - this is about your preferences and who would be the best fit for your clients and team.',
        },
        {
          id: 'priority',
          question: 'What is your priority regarding succession?',
          type: 'radio',
          required: true,
          options: [
            { value: 'business_after', label: "What happens to the business after I'm gone" },
            { value: 'me_after', label: 'What happens to me after the business is gone' },
            { value: 'balance', label: 'Finding the right balance' },
          ],
          hint: 'Your priority will significantly influence the type of succession plan you develop and the buyers you consider.',
        },
        {
          id: 'other_priority',
          question: 'If you have another priority not listed above, please specify',
          type: 'text',
          required: false,
          multiline: true,
          hint: 'Every business owner has unique priorities - feel free to describe what matters most to you about the succession process.',
        },
        {
          id: 'business_impression',
          question: 'If you were a potential purchaser of your business, how impressed are you with it?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Try to be objective and consider your business\'s reputation, client relationships, financial performance, and operational efficiency.',
        },
        {
          id: 'deterring_factors',
          question: 'Are there any factors that might deter a potential purchaser?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Common deterrents include client concentration, outdated systems, compliance issues, or team members nearing retirement.',
        },
        {
          id: 'improvement_steps',
          question: 'What steps do you need to take to make improvements before you bring your business to the market?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider operational, financial, and structural improvements that could increase your business\'s value and attractiveness.',
        },
        {
          id: 'business_strengths_weaknesses',
          question: 'What are the strengths and weaknesses of your business? (Consider management, financial performance, sales and marketing, key customers, growth prospects, capacity, major contracts, facilities, systems, company/brand reputation, licensing and royalty agreements)',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'A thorough SWOT analysis helps identify what to emphasize when marketing your business and what to improve before selling.',
        },
        {
          id: 'meets_personal_objectives',
          question: 'Does this business still meet your personal objectives?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'partially', label: 'Partially' },
          ],
          hint: 'If your business no longer aligns with your personal goals, it may be time to consider transitioning sooner rather than later.',
        },
      ],
    },
    {
      id: 'emotional_readiness',
      title: 'Emotional and Financial Readiness',
      description: 'This section assesses your emotional and financial preparedness for transitioning out of your business.',
      questions: [
        {
          id: 'emotional_enthusiasm',
          question: 'How would you rate your emotional enthusiasm for transitioning out of your business?',
          type: 'radio',
          required: true,
          options: [
            { value: 'low', label: "Low - I'm totally defined by my business" },
            { value: 'medium', label: 'Medium - My life extends beyond my practice' },
            { value: 'high', label: "High - I can't wait to get out of this business" },
          ],
          hint: 'Your emotional readiness is as important as financial readiness. Many owners underestimate the emotional impact of stepping away.',
        },
        {
          id: 'emotional_details',
          question: 'Please check all statements that apply to your emotional state regarding transition',
          type: 'checkbox',
          required: true,
          options: [
            { value: 'totally_defined', label: "I'm totally defined by my business" },
            { value: 'no_next_idea', label: "I have no idea what to do next" },
            { value: 'business_needs_me', label: "The business couldn't run without me" },
            { value: 'need_perks', label: "I couldn't live without the perks and recognition" },
            { value: 'life_beyond', label: "My life extends beyond my practice" },
            { value: 'want_time', label: "I'd like more time to pursue other interests" },
            { value: 'good_team', label: "I have a good team to support the business" },
            { value: 'content_accomplished', label: "I am content with what I've accomplished and the fun I've had" },
            { value: 'cant_wait', label: "I can't wait to get out of this business!" },
            { value: 'passion_pursue', label: "I have a real passion I'd like to pursue" },
            { value: 'proud_accomplished', label: "I'm very proud of what I've accomplished" },
            { value: 'like_mentor', label: "I'd like to coach/mentor someone to carry on in my place" },
          ],
          hint: 'These statements help identify your current emotional state and areas where you might need to prepare for transition.',
        },
        {
          id: 'financial_capability',
          question: 'How would you rate your financial capability to transition out of your business?',
          type: 'radio',
          required: true,
          options: [
            { value: 'low', label: 'Low - I have little or no investments outside my business' },
            { value: 'medium', label: 'Medium - I have some personal assets to supplement proceeds' },
            { value: 'high', label: 'High - I have substantial personal assets outside the business' },
          ],
          hint: 'Financial readiness affects your timeline and flexibility in selecting a successor and structuring the deal.',
        },
        {
          id: 'financial_details',
          question: 'Please check all statements that apply to your financial situation regarding transition',
          type: 'checkbox',
          required: true,
          options: [
            { value: 'little_investments', label: "I have little or no investments outside my business" },
            { value: 'lifestyle_depends', label: "My lifestyle depends entirely on how well my practice is doing" },
            { value: 'business_pays_personal', label: "My business pays for a lot of my personal expenses" },
            { value: 'counting_fund_retirement', label: "I'm counting on my business to fund my retirement" },
            { value: 'some_assets', label: "I have some personal assets to supplement proceeds from selling my practice" },
            { value: 'practice_pays_enough', label: "My practice pays me more than enough to support my lifestyle" },
            { value: 'obligations_not_onerous', label: "My financial obligations are not onerous" },
            { value: 'sale_fund_large_part', label: "The sale of my business will fund a large part of my retirement" },
            { value: 'substantial_assets', label: "I have substantial personal assets outside the business" },
            { value: 'dont_need_maintain', label: "I don't need to sell my practice to maintain my lifestyle" },
            { value: 'clients_more_important', label: "What happens to my clients is more important than the money" },
          ],
          hint: 'Your financial situation will impact your negotiating position and the type of deal structure that works for you.',
        },
        {
          id: 'overall_readiness',
          question: 'How would you describe your overall readiness for succession?',
          type: 'radio',
          required: true,
          options: [
            { value: 'low_emotional_low_financial', label: 'Low emotional + low financial readiness' },
            { value: 'high_emotional_low_financial', label: 'High emotional + low financial readiness' },
            { value: 'low_emotional_high_financial', label: 'Low emotional + high financial readiness' },
            { value: 'high_emotional_high_financial', label: 'High emotional + high financial readiness' },
          ],
          hint: 'The combination of emotional and financial readiness determines your overall preparedness for succession.',
        },
        {
          id: 'readiness_explanation',
          question: 'Why did you rank yourself this way? What do you need to do to change your rank?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Identifying specific actions to improve your readiness can help you create a pre-succession action plan.',
        },
      ],
    },
    {
      id: 'business_valuation',
      title: 'Business Valuation',
      description: 'This section gathers information relevant to determining the value of your practice.',
      questions: [
        {
          id: 'recent_valuation',
          question: 'Do you have a recent valuation of your business?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ],
          hint: 'A professional valuation provides an objective baseline and helps identify value drivers and areas for improvement.',
        },
        {
          id: 'outside_buyer_worth',
          question: 'How much do you think the business is worth if you sold it to an outside buyer?',
          type: 'text',
          required: true,
          placeholder: 'Enter amount in dollars',
          hint: 'External buyers may pay a premium for a well-established business with good systems and a diverse client base.',
        },
        {
          id: 'family_worth',
          question: 'How much do you think the business is worth if you sold it to a family member?',
          type: 'text',
          required: true,
          placeholder: 'Enter amount in dollars',
          hint: 'Family transactions often occur at a discount, balancing financial needs with family relationships and legacy goals.',
        },
        {
          id: 'fee_based_percentage',
          question: 'What percentage of your business is fee-based versus commission-based?',
          type: 'slider',
          required: true,
          min: 0,
          max: 100,
          step: 5,
          label: 'Percentage fee-based',
          hint: 'Recurring revenue from fee-based business is typically valued higher than commission-based revenue.',
        },
        {
          id: 'average_fee',
          question: 'What is the average fee for your fee-based business?',
          type: 'text',
          required: true,
          placeholder: 'Enter amount in dollars',
          hint: 'Higher average fees may indicate more sophisticated services and potentially higher business value.',
        },
        {
          id: 'fee_return_rate',
          question: 'What is the average rate of return for the fee-based portion of the business?',
          type: 'text',
          required: true,
          placeholder: 'Enter percentage',
          hint: 'Consistent, competitive returns demonstrate effective investment management and can increase practice value.',
        },
        {
          id: 'products_services',
          question: 'What products and services do you offer (financial planning, insurance, retirement plans)? What is the percentage makeup of each?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'A diverse product mix can be attractive to buyers, showing versatility and multiple revenue streams.',
        },
        {
          id: 'competitive_advantages',
          question: 'What are the key sustainable competitive advantages of your business compared to others in the same industry?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Unique advantages like specialized expertise, proprietary systems, or exclusive partnerships significantly impact value.',
        },
        {
          id: 'growth_opportunities',
          question: 'Are there tangible growth opportunities that a potential purchaser could capture?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Untapped markets, expansion possibilities, or cross-selling opportunities make your business more attractive to buyers.',
        },
        {
          id: 'relationship_management',
          question: 'Are relationships with customers and suppliers managed personally or by various team members?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Relationships spread across team members rather than concentrated with the owner increase business transferability.',
        },
        {
          id: 'profitability_drivers',
          question: 'Can you easily convey the qualitative and quantitative profitability and cash flow drivers of your business?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'partially', label: 'Partially' },
          ],
          hint: 'Understanding and clearly communicating what drives your business\'s success is crucial during the sale process.',
        },
        {
          id: 'financial_reliability',
          question: 'How reliable is your historical and current financial information? Can financial data and reports be accessed in real-time?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Clean, accessible financial records increase buyer confidence and can streamline the due diligence process.',
        },
        {
          id: 'financial_details',
          question: 'Please provide details on any non-recurring or extraordinary amounts in your financial statements, as well as non-arm\'s length or discretionary expenses and transactions.',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Transparency about unusual financial items helps establish trust and presents a clearer picture of true business performance.',
        },
        {
          id: 'renewal_trailer_info',
          question: 'Do you have renewal/trailer information for the past 3 years?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'partial', label: 'Partial information' },
          ],
          hint: 'Historical renewal rates provide insight into client loyalty and the sustainability of revenue streams.',
        },
        {
          id: 'business_plans',
          question: 'Do you have current business plans, budgets, and projections?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'outdated', label: 'Yes, but they are outdated' },
          ],
          hint: 'Current strategic documents demonstrate business organization and provide a roadmap for potential buyers.',
        },
        {
          id: 'fixed_assets',
          question: 'Do you have a description of all major fixed assets, including acquisition year, original cost, and depreciated cost?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'partial', label: 'Partial information' },
          ],
          hint: 'Detailed asset records facilitate accurate valuation and smooth ownership transfer during succession.',
        },
      ],
    },
    {
      id: 'business_preparation',
      title: 'Preparing the Business for Transition',
      description: 'This section focuses on steps to make your business more attractive for succession.',
      questions: [
        {
          id: 'strategic_plan',
          question: 'Do you have a strategic plan with measurable goals and milestones for the coming years?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'in_progress', label: 'In progress' },
          ],
          hint: 'A well-documented strategic plan demonstrates forward thinking and provides direction for a successor.',
        },
        {
          id: 'processes_repeatable',
          question: 'Are your business processes repeatable and teachable, reducing reliance on your personal input?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'partially', label: 'Partially' },
          ],
          hint: 'Documented, standardized processes increase business transferability and reduce transition risks.',
        },
        {
          id: 'focused_improvement',
          question: 'Have you focused on improving profitability, management information, and productivity?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'somewhat', label: 'Somewhat' },
          ],
          hint: 'Consistent profitability with good margins and efficient operations significantly increases business value.',
        },
        {
          id: 'diversified_bases',
          question: 'Have you diversified your customer and supplier bases?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'in_progress', label: 'In progress' },
          ],
          hint: 'Reduced dependence on a few key clients or suppliers lowers business risk and increases attractiveness to buyers.',
        },
        {
          id: 'non_operating_assets',
          question: 'Have you removed all non-operating assets from the business?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'in_progress', label: 'In progress' },
          ],
          hint: 'Separating personal assets from business assets creates a cleaner financial picture for valuation and sale.',
        },
        {
          id: 'tax_restructuring',
          question: 'Have you considered restructuring the company to minimize taxes on sale?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'consulting_advisors', label: 'Currently consulting with advisors' },
          ],
          hint: 'Tax planning well in advance of a sale can significantly impact your after-tax proceeds from the transaction.',
        },
        {
          id: 'benchmark_performance',
          question: 'Do you regularly benchmark your business performance against the industry?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'occasionally', label: 'Occasionally' },
          ],
          hint: 'Comparison to industry standards helps identify strengths to highlight and weaknesses to address before sale.',
        },
      ],
    },
    {
      id: 'team_client_considerations',
      title: 'Team and Client Considerations',
      description: 'This section addresses the human aspects of succession planning.',
      questions: [
        {
          id: 'team_impact',
          question: 'Have you considered how the succession plan will impact your team members?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'somewhat', label: 'Somewhat' },
          ],
          hint: 'Your team\'s reaction to succession can significantly affect client retention and business continuity during transition.',
        },
        {
          id: 'swift_analysis',
          question: "What are your firm's strengths, weaknesses, integration opportunities, future-state needs, and potential threats (SWIFT analysis)?",
          type: 'text',
          required: true,
          multiline: true,
          hint: 'A comprehensive SWIFT analysis helps both you and potential successors understand the business\'s position and potential.',
        },
        {
          id: 'firm_culture',
          question: "How would you describe your firm's culture? What values are important to maintain during the transition?",
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Cultural alignment between your firm and a successor is crucial for successful integration and client retention.',
        },
        {
          id: 'client_notice_timeframe',
          question: 'How far in advance do you think your clients would want to know about your retirement plans?',
          type: 'radio',
          required: true,
          options: [
            { value: 'less_than_6_months', label: 'Less than 6 months' },
            { value: '6_12_months', label: '6-12 months' },
            { value: '1_2_years', label: '1-2 years' },
            { value: 'more_than_2_years', label: 'More than 2 years' },
          ],
          hint: 'Client communication timing is delicate - too early may cause anxiety, too late may feel rushed and inconsiderate.',
        },
        {
          id: 'team_understands_clients',
          question: "Do you feel the rest of your team understands and fulfills your clients' needs as you do?",
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'partially', label: 'Partially' },
          ],
          hint: 'Team members who already understand client needs can help maintain continuity during the transition process.',
        },
        {
          id: 'client_communication_plan',
          question: 'Have you started thinking about a client communication plan for your succession?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'early_stages', label: 'In the early stages' },
          ],
          hint: 'A thoughtful communication strategy can reduce client anxiety and increase retention during ownership transition.',
        },
        {
          id: 'key_messages',
          question: 'What key messages do you want to convey to your clients regarding the transition and your successor?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Focus on continuity of service, shared values, and how the transition benefits clients rather than just yourself.',
        },
        {
          id: 'successor_client_meetings',
          question: 'Have you considered having your successor accompany you to client meetings to facilitate the relationship building process?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not_applicable', label: 'Not applicable yet' },
          ],
          hint: 'Joint meetings allow for gradual relationship transfer and demonstrate your confidence in your successor.',
        },
      ],
    },
    {
      id: 'successor_considerations',
      title: 'Successor Considerations',
      description: 'This section explores your thoughts on who might take over your business.',
      questions: [
        {
          id: 'successor_identified',
          question: 'Have you identified the person you intend to hand over your business to?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ],
          hint: 'Having a successor in mind allows for more targeted preparation and potentially a smoother transition process.',
        },
        {
          id: 'sale_or_groom',
          question: 'Based on your analysis, do you envision an outright sale of your business or grooming a successor to take over?',
          type: 'radio',
          required: true,
          options: [
            { value: 'sale', label: 'Outright sale' },
            { value: 'groom_successor', label: 'Grooming a successor' },
            { value: 'undecided', label: 'Undecided' },
          ],
          hint: 'Each approach has different timelines, preparation requirements, and personal involvement levels during transition.',
        },
        {
          id: 'successor_type',
          question: 'What type of successor are you considering?',
          type: 'radio',
          required: true,
          options: [
            { value: 'internal', label: 'Someone within your current practice' },
            { value: 'family', label: 'A family member' },
            { value: 'client', label: 'Someone amongst your clientele' },
            { value: 'external', label: 'Peers/industry candidates' },
            { value: 'undecided', label: "Haven't decided yet" },
          ],
          hint: 'Different successor types bring different advantages in terms of client knowledge, industry experience, and cultural fit.',
        },
        {
          id: 'ideal_buyer',
          question: 'If considering an external successor, what characteristics, skills, experience, education, and other attributes would your ideal buyer possess?',
          type: 'text',
          required: false,
          multiline: true,
          conditional: {
            dependsOn: 'successor_type',
            showIf: 'external',
          },
          hint: 'Creating a detailed profile of your ideal successor helps focus your search and evaluate potential candidates.',
        },
        {
          id: 'future_leader',
          question: 'What type of leader will be needed to take your business into the future?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Consider industry trends and client needs when describing the leadership qualities needed for future success.',
        },
        {
          id: 'successor_responsibilities',
          question: "What will be the successor's areas of authority, responsibility, and accountability?",
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Clearly defined roles and expectations help avoid misunderstandings and conflicts during transition.',
        },
        {
          id: 'internal_successor_conversations',
          question: 'If you have potential internal successors, have you had conversations with them to gauge their interest in buying the practice?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not_applicable', label: 'Not applicable' },
          ],
          hint: 'Early, transparent conversations can reveal interest levels and allow potential successors to prepare financially.',
        },
        {
          id: 'future_success_requirements',
          question: 'What knowledge, ability, skills, habits, and experience are needed for your business\'s future success (in the next 10-25 years)?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Future-oriented thinking helps identify successor qualities needed to adapt to evolving industry and market conditions.',
        },
        {
          id: 'industry_trends',
          question: 'What are the trends in your industry or sector, and how can a successor capitalize on them?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Understanding industry direction helps identify successors with the right skills to leverage emerging opportunities.',
        },
      ],
    },
    {
      id: 'post_succession',
      title: 'Post-Succession Plans',
      description: 'This section explores your vision for your life after you transition from the business.',
      questions: [
        {
          id: 'next_stage_mapped',
          question: 'Have you started to map out what you want in the next stage of your life?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'somewhat', label: 'Somewhat' },
          ],
          hint: 'Having a well-thought-out plan for your next chapter can ease the emotional transition of letting go of your business.',
        },
        {
          id: 'post_sale_involvement',
          question: 'Do you plan to stay at your firm post-sale in a part-time or consultant capacity?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes_lt_1', label: 'Yes, less than 1 year' },
            { value: 'yes_1_3', label: 'Yes, 1-3 years' },
            { value: 'yes_gt_3', label: 'Yes, more than 3 years' },
            { value: 'no', label: 'No' },
            { value: 'undecided', label: "Haven't decided yet" },
          ],
          hint: 'Continued involvement can ease the transition for clients and staff while providing you with a gradual change.',
        },
        {
          id: 'retirement_vision',
          question: 'What connections, purpose, and joy are you seeking in your retirement?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Fulfilling retirement involves more than financial security - consider relationships, meaning, and enjoyment.',
        },
        {
          id: 'post_business_life',
          question: 'What will your life look like once you are no longer actively involved in the business?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Envision daily routines, activities, projects, and relationships that will replace your business involvement.',
        },
      ],
    },
    {
      id: 'deal_structure',
      title: 'Deal Structure and Financing',
      description: 'This section delves into the financial aspects of a potential sale.',
      questions: [
        {
          id: 'sale_expectations',
          question: 'What are your expectations regarding the sale price and conditions?',
          type: 'text',
          required: true,
          multiline: true,
          hint: 'Be realistic but confident in your expectations, considering both financial needs and market conditions.',
        },
        {
          id: 'deal_structures',
          question: 'Have you considered different deal structures (e.g., sale of assets, earnout, stock sale)?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'consulting_advisors', label: 'Currently consulting with advisors' },
          ],
          hint: 'The structure of your deal can significantly impact tax implications, risk allocation, and total proceeds.',
        },
        {
          id: 'vendor_financing',
          question: 'Are you open to contributing to the financing of the sale with a vendor note?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'maybe', label: 'Maybe, depends on the terms' },
          ],
          hint: 'Offering financing can expand your pool of potential buyers and potentially increase the sale price.',
        },
        {
          id: 'tax_implications',
          question: 'Have you considered the tax implications of different sale structures?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'consulting_advisors', label: 'Currently consulting with advisors' },
          ],
          hint: 'Tax planning can significantly impact your net proceeds - consult with tax professionals well before a sale.',
        },
        {
          id: 'insurance_coverage',
          question: 'Do you have existing insurance coverage that needs to be re-evaluated in light of a potential sale?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure' },
          ],
          hint: 'Insurance needs often change during transition, including considerations for tail coverage and personal policies.',
        },
        {
          id: 'legal_documents',
          question: 'Do you have a will, power of attorney, and shareholders\' agreement that have been recently reviewed and updated?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'outdated', label: 'Yes, but they are outdated' },
          ],
          hint: 'Updated legal documents are crucial for protecting your interests and ensuring a smooth transition if unexpected events occur.',
        },
        {
          id: 'dispute_handling',
          question: 'Have you thought about how disputes between shareholders (if any remain) will be handled during and after the transition?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not_applicable', label: 'Not applicable' },
          ],
          hint: 'Clear dispute resolution mechanisms can prevent costly conflicts and protect relationships during transition.',
        },
      ],
    },
  ];
  
  export const getQuestionById = (id) => {
    for (const section of questionnaireSchema) {
      const question = section.questions.find(q => q.id === id);
      if (question) return question;
    }
    return null;
  };
  
  export const getSectionById = (id) => {
    return questionnaireSchema.find(section => section.id === id) || null;
  }; 